import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Dashboard,
  ShoppingCart,
  Category,
  LocalShipping,
  Storefront,
  Settings,
  ExpandMore,
  Storage,
  ExpandLess,
  ShoppingBag,
  People,
  Logout,
  AccountBox,
  MenuOpen,
  Menu,
} from "@mui/icons-material";

const isPathActive = (pathname, itemPath) => {
  if (!itemPath) return false;
  if (itemPath === "/") return pathname === "/";
  return pathname === itemPath || pathname.startsWith(itemPath + "/");
};

const Sidebar = () => {
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const toggleMenu = (menuName) => {
    setExpandedMenu((prev) => (prev === menuName ? null : menuName));
  };

  const handleToggleCollapsed = () => {
    setCollapsed((c) => !c);
    // close expanded menu when collapsing (clean)
    setExpandedMenu(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("auth");
    localStorage.removeItem("admin");
    localStorage.removeItem("token");
    localStorage.removeItem("adminToken");
    localStorage.removeItem("role");
    localStorage.removeItem("userEmail");

    navigate("/login", { replace: true });
  };

  const menuItems = useMemo(
    () => [
      { name: "Dashboard", path: "/", icon: <Dashboard fontSize="small" /> },

      {
        name: "Orders",
        icon: <ShoppingCart fontSize="small" />,
        subMenu: [
          { name: "All Orders", path: "/orders/all" },
          { name: "Pending Orders", path: "/orders/pending" },
          { name: "Confirmed Orders", path: "/orders/confirmed" },
          { name: "Shipped Orders", path: "/orders/shipped" },
          { name: "Delivered Orders", path: "/orders/delivered" },
          { name: "Cancelled Orders", path: "/orders/cancelled" },
        ],
      },
         { name: "Bulk Orders",path: "/bulk-orders",icon: <ShoppingCart fontSize="small" />,},

      {
        name: "Manage Categories",
        icon: <Category fontSize="small" />,
        subMenu: [
          { name: "Category", path: "/category" },
          { name: "Subcategories", path: "/subcategories" },
        ],
      },

      {
        name: "Manage Products",
        icon: <ShoppingBag fontSize="small" />,
        subMenu: [
          { name: "Products", path: "/products" },
          { name: "Create Product", path: "/products/create" },
          // { name: "Seller Products", path: "/seller-products" },
          { name: "Waiting for Approval", path: "/waiting-for-approval" },
        ],
      },

      {
        name: "Admins",
        icon: <People fontSize="small" />,
        subMenu: [{ name: "All Admins", path: "/admins/all" }],
      },

      {
        name: "Users",
        icon: <People fontSize="small" />,
        subMenu: [
          { name: "All Users", path: "/users/all" },
          { name: "Create User", path: "/users/create" },
          { name: "Interested Buyer", path: "/users/interested" },
        ],
      },

      {
        name: "Sellers",
        icon: <People fontSize="small" />,
        subMenu: [{ name: "All Sellers", path: "/sellers/all" },
        { name: "Seller Products", path: "/seller-products" },

        ],
      },

      {
        name: "Accounts",
        icon: <AccountBox fontSize="small" />,
        subMenu: [
          // { name: "Seller Product Payments", path: "/accounts/seller" },
          { name: "Seller Registration Payments", path: "/accounts/seller/registration" },
        ],
      },

      // {
      //   name: "All Datas",
      //   icon: <Storage fontSize="small" />,
      //   subMenu: [
      //     { name: "Users", path: "/all-users" },
      //     { name: "Addresses", path: "/address" },
      //     { name: "Admin", path: "/admin" },
      //     { name: "Carts", path: "/carts" },
      //     { name: "Categories", path: "/categories" },
      //     { name: "SubCategories", path: "/sub-categories" },
      //     { name: "Delivery Charges", path: "/delivery-charges" },
      //     { name: "Interested Users", path: "/interested-users" },
      //     { name: "Other Payments", path: "/other-payments" },
      //     { name: "Order", path: "/order" },
      //     { name: "Order Tracking", path: "/order-tracking" },
      //     { name: "Seller Payments Product", path: "/seller-product-payments" },
      //     { name: "Sellers", path: "/sellers" },
      //     { name: "Wishlist", path: "/wishlist" },
      //   ],
      // },

      { name: "Locations", path: "/locations", icon: <LocalShipping fontSize="small" /> },
      // { name: "Sale By Sellers", path: "/salebysellers", icon: <LocalShipping fontSize="small" /> },
      { name: "POS", path: "/pos", icon: <Storefront fontSize="small" /> },
      { name: "Settings", path: "/settings", icon: <Settings fontSize="small" /> },
   
    ],
    []
  );

  // ✅ active group for submenu parents
  const groupIsActive = (item) => {
    if (item.subMenu?.length) return item.subMenu.some((s) => isPathActive(pathname, s.path));
    return isPathActive(pathname, item.path);
  };

  return (
    <aside
      className={[
        "bg-white text-gray-900 min-h-screen border-r border-gray-200",
        "transition-all duration-200",
        collapsed ? "w-20" : "w-64",
      ].join(" ")}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 overflow-hidden">

          {!collapsed && (
            <div className="leading-tight">
              <div className="text-base font-bold">Admin Panel</div>
              <div className="text-xs text-gray-500">BuildPro Admin</div>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={handleToggleCollapsed}
          className="p-2 rounded-lg hover:bg-gray-100 transition"
          title={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? <Menu fontSize="small" /> : <MenuOpen fontSize="small" />}
        </button>
      </div>

      {/* Menu */}
      <nav className="p-3">
        <ul className="space-y-1">
          {menuItems.map((item, index) => {
            const hasSub = !!item.subMenu?.length;
            const active = groupIsActive(item);

            // Collapsed mode: show only icons; parent items won't expand
            if (collapsed) {
              const to = hasSub ? item.subMenu[0]?.path || "/" : item.path;

              return (
                <li key={index}>
                  <Link
                    to={to}
                    className={[
                      "flex items-center justify-center h-11 rounded-lg",
                      "transition",
                      active ? "bg-blue-600 text-white" : "hover:bg-gray-100 text-gray-700",
                    ].join(" ")}
                    title={item.name}
                  >
                    <span className={active ? "text-white" : "text-gray-600"}>{item.icon}</span>
                  </Link>
                </li>
              );
            }

            // Expanded mode (normal)
            return (
              <li key={index}>
                {hasSub ? (
                  <div>
                    <button
                      type="button"
                      onClick={() => toggleMenu(item.name)}
                      className={[
                        "w-full flex items-center gap-3 px-3 py-2 rounded-lg",
                        "transition",
                        active ? "bg-blue-50 text-blue-700" : "hover:bg-gray-100",
                      ].join(" ")}
                    >
                      <span className={active ? "text-blue-700" : "text-gray-600"}>{item.icon}</span>
                      <span className="text-sm font-semibold flex-1 text-left">{item.name}</span>

                      {expandedMenu === item.name ? (
                        <ExpandLess fontSize="small" className="text-gray-500" />
                      ) : (
                        <ExpandMore fontSize="small" className="text-gray-500" />
                      )}
                    </button>

                    {expandedMenu === item.name && (
                      <ul className="mt-1 ml-2 border-l border-gray-200 pl-3 space-y-1">
                        {item.subMenu.map((subItem, subIndex) => {
                          const subActive = isPathActive(pathname, subItem.path);

                          return (
                            <li key={subIndex}>
                              <Link
                                to={subItem.path}
                                className={[
                                  "block px-3 py-2 rounded-lg text-sm",
                                  "transition",
                                  subActive
                                    ? "bg-blue-600 text-white"
                                    : "text-gray-700 hover:bg-gray-100",
                                ].join(" ")}
                              >
                                {subItem.name}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className={[
                      "flex items-center gap-3 px-3 py-2 rounded-lg",
                      "transition",
                      active ? "bg-blue-600 text-white" : "hover:bg-gray-100",
                    ].join(" ")}
                  >
                    <span className={active ? "text-white" : "text-gray-600"}>{item.icon}</span>
                    <span className="text-sm font-semibold">{item.name}</span>
                  </Link>
                )}
              </li>
            );
          })}

          {/* Logout */}
          <li className="pt-2 mt-2 border-t border-gray-200">
            {collapsed ? (
              <button
                onClick={handleLogout}
                className="w-full h-11 rounded-lg hover:bg-red-50 transition flex items-center justify-center"
                title="Logout"
              >
                <Logout fontSize="small" className="text-red-600" />
              </button>
            ) : (
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-red-50 transition text-left"
              >
                <Logout fontSize="small" className="text-red-600" />
                <span className="text-sm font-semibold text-red-600">Logout</span>
              </button>
            )}
          </li>
        </ul>
      </nav>
    </aside>
  );
};

export default Sidebar;
























// import React, { useState } from "react";
// import { Link, useNavigate } from "react-router-dom";
// import {
//   Dashboard,
//   ShoppingCart,
//   Category,
//   LocalShipping,
//   Storefront,
//   Settings,
//   ExpandMore,
//   Storage,
//   ExpandLess,
//   ShoppingBag,
//   People,
//   Logout,
//   AccountBox,
// } from "@mui/icons-material";

// const Sidebar = () => {
//   const [expandedMenu, setExpandedMenu] = useState(null);
//   const navigate = useNavigate();

//   const toggleMenu = (menuName) => {
//     setExpandedMenu(expandedMenu === menuName ? null : menuName);
//   };

//   const handleLogout = () => {
//     // ✅ remove new + old keys (safe)
//     localStorage.removeItem("auth");
//     localStorage.removeItem("token");
//     localStorage.removeItem("role");
//     localStorage.removeItem("admin");

//     navigate("/login", { replace: true });
//   };

//   const menuItems = [
//     { name: "Dashboard", path: "/", icon: <Dashboard /> },

//     {
//       name: "Orders",
//       icon: <ShoppingCart />,
//       subMenu: [
//         { name: "All Orders", path: "/orders/all" },
//         { name: "Pending Orders", path: "/orders/pending" },
//         { name: "Confirmed Orders", path: "/orders/confirmed" },
//         { name: "Shipped Orders", path: "/orders/shipped" },
//         { name: "Delivered Orders", path: "/orders/delivered" },
//         { name: "Canceled Orders", path: "/orders/canceled" },
//       ],
//     },

//     {
//       name: "Manage Categories",
//       icon: <Category />,
//       subMenu: [
//         { name: "Category", path: "/category" },
//         { name: "Subcategories", path: "/subcategories" },
//       ],
//     },

//     {
//       name: "Manage Products",
//       icon: <ShoppingBag />,
//       subMenu: [
//         { name: "Products", path: "/products" },
//         { name: "Create Product", path: "/products/create" },
//         { name: "Seller Products", path: "/seller-products" },
//         { name: "Waiting for Approval", path: "/waiting-for-approval" },
//       ],
//     },

//     {
//       name: "Admins",
//       icon: <People />,
//       subMenu: [{ name: "All Admins", path: "/admins/all" }],
//     },

//     {
//       name: "Users",
//       icon: <People />,
//       subMenu: [
//         { name: "All Users", path: "/users/all" },
//         { name: "Create User", path: "/users/create" },
//         { name: "Interested Buyer", path: "/users/interested" },
//         // { name: "New Users", path: "/users/new" },
//       ],
//     },

//     {
//       name: "Sellers",
//       icon: <People />,
//       subMenu: [
//         { name: "All Sellers", path: "/sellers/all" },
//         // ❌ remove until you create route
//         // { name: "Create Seller", path: "/sellers/create" },
//       ],
//     },

//     {
//       name: "Accounts",
//       icon: <AccountBox />,
//       subMenu: [
//         { name: "Seller Product Payments", path: "/accounts/seller" },
//         { name: "Seller Registration Payments", path: "/accounts/seller/registration" },
//         // ❌ remove until you create route
//         // { name: "User Order Payments", path: "/accounts/user/order" },
//       ],
//     },

//     {
//       name: "All Datas",
//       icon: <Storage />,
//       subMenu: [
//         // ✅ fixed path casing
//         { name: "Users", path: "/all-users" },
//         { name: "Addresses", path: "/address" },
//         { name: "Admin", path: "/admin" },
//         { name: "Carts", path: "/carts" },
//         { name: "Categories", path: "/categories" },
//         { name: "SubCategories", path: "/sub-categories" },
//         { name: "Delivery Charges", path: "/delivery-charges" },
//         { name: "Interested Users", path: "/interested-users" },
//         { name: "Other Payments", path: "/other-payments" },
//         { name: "Order", path: "/order" },
//         { name: "Order Tracking", path: "/order-tracking" },
//         { name: "Seller Payments Product", path: "/seller-product-payments" },
//         { name: "Sellers", path: "/sellers" },
//         { name: "Wishlist", path: "/wishlist" },

//         // ❌ remove until you create route
//         // { name: "User Order Payments", path: "/user-payments" },
//       ],
//     },

//     { name: "Locations", path: "/locations", icon: <LocalShipping /> },
//     { name: "Sale By Sellers", path: "/salebysellers", icon: <LocalShipping /> },
//     { name: "POS", path: "/pos", icon: <Storefront /> },
//     { name: "Settings", path: "/settings", icon: <Settings /> },
//   ];

//   return (
//     <div className="bg-white text-black w-64 min-h-screen p-6 shadow-xl">
//       <h2 className="text-3xl font-bold mb-8 text-center text-black">
//         Admin Panel
//       </h2>

//       <ul className="space-y-4">
//         {menuItems.map((item, index) => (
//           <li key={index} className="cursor-pointer">
//             {item.subMenu ? (
//               <div>
//                 <div
//                   onClick={() => toggleMenu(item.name)}
//                   className="flex items-center p-3 rounded-lg hover:bg-gray-100 hover:text-blue-400 transition duration-300"
//                 >
//                   <span className="text-blue-400 mr-3">{item.icon}</span>
//                   <span className="font-medium flex-grow">{item.name}</span>
//                   {expandedMenu === item.name ? (
//                     <ExpandLess className="text-blue-400" />
//                   ) : (
//                     <ExpandMore className="text-blue-400" />
//                   )}
//                 </div>

//                 {expandedMenu === item.name && (
//                   <ul className="ml-6 mt-2 space-y-2">
//                     {item.subMenu.map((subItem, subIndex) => (
//                       <li key={subIndex}>
//                         <Link
//                           to={subItem.path}
//                           className="flex items-center p-2 rounded-lg hover:bg-gray-400 transition duration-300 text-black"
//                         >
//                           {subItem.name}
//                         </Link>
//                       </li>
//                     ))}
//                   </ul>
//                 )}
//               </div>
//             ) : (
//               <Link
//                 to={item.path}
//                 className="flex items-center p-3 rounded-lg hover:bg-gray-100 hover:text-blue-400 transition duration-300"
//               >
//                 <span className="text-blue-400 mr-3">{item.icon}</span>
//                 <span className="font-medium">{item.name}</span>
//               </Link>
//             )}
//           </li>
//         ))}

//         <li>
//           <button
//             onClick={handleLogout}
//             className="flex items-center p-3 rounded-lg hover:bg-red-600 transition duration-300 w-full text-left"
//           >
//             <Logout className="text-red-400 mr-3" />
//             <span className="font-medium">Logout</span>
//           </button>
//         </li>
//       </ul>
//     </div>
//   );
// };

// export default Sidebar;